<template>
  <div class="dashboard-content">
    <b-container>
      <page-headline>Stocklist Upload</page-headline>
      <b-row>
        <b-col>
          <p>Sell more watches to us by uploading your Stocklist. To enable our system to process your Stocklist:</p>
          <ul>
            <li>please use our Excel template and upload one Stocklist file that contains all watches from all brands
              that you have in stock
            </li>
            <li>please update your Stocklist at least once every 7 days (more often is better of course)</li>
          </ul>
          <p>We only use your data internally to purchase watches from you and CHRONEXT will never share your Stocklist
            with third parties.</p>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col class="d-flex">
          <b-form-file
              class="mr-3"
              accept=".xlsx"
              size="lg"
              v-model="stockList"
              ref="stockListFileInput"
              :state="null"
              placeholder="Choose a file or drop it here..."
              drop-placeholder="Drop file here..."
          ></b-form-file>
          <b-button :disabled="isSaving || !this.stockList" @click.prevent="uploadStockList">UPLOAD</b-button>
        </b-col>
      </b-row>
      <b-row class="mb-4">
        <b-col>
          <b-alert :show="isSuccess" variant="success">Upload successful</b-alert>
          <b-alert :show="isFailed" variant="danger">Upload failed</b-alert>
          <b-alert :show="isSaving" variant="info"><span class="d-flex align-items-center">
            <b-spinner class="mr-2" small/>Uploading file...</span>
          </b-alert>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <h2>Past Uploads</h2>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <p>Here you can see the history of Stocklist uploads that you have made and their status. You can also
            download the files you have uploaded to us for reference and easy updating.</p>
        </b-col>
      </b-row>
      <b-table :items="this.getUploadedStockLists" :fields="uploadsDataFields">
        <template v-slot:cell(originalName)="data">
          {{data.item.originalName}}
          <b-icon icon="download" class="download-button ml-2" scale="1.25" variant="info"
                  @click.prevent="() => downloadFile('stockFile', data.item.uniqueName, data.item.originalName)"/>
        </template>
        <template v-slot:cell(status)="data">
          <b-badge v-if="data.item.status === 'completed'" variant="success">{{data.item.status}}</b-badge>
          <b-badge v-else-if="data.item.status === 'failed'" variant="danger">{{data.item.status}}</b-badge>
          <b-badge v-else-if="data.item.status === 'pending'" variant="warning">{{data.item.status}}</b-badge>
          <b-badge v-else variant="info">{{data.item.status}}</b-badge>
          <b-button
              variant="link"
              size="sm" class="ml-2"
              @click.prevent="() => downloadFile('importLog', data.item.uniqueName, `report_${data.item.originalName.replace(/\.xlsx$/, '.csv')}`)">
            <b-icon icon="download" class="download-button mr-1" variant="info"/>
            Download log
          </b-button>
        </template>
      </b-table>
    </b-container>
  </div>
</template>

<script>
import PageHeadline from 'Components/common/PageHeadline';
import constants from 'Constants/constants';
import { hasAnyOfTheseRoles } from 'src/auth';
import apiAuth from 'src/clients/apiAuth';
import store from 'src/store';

const STATUS_INITIAL = 10;
const STATUS_SAVING = 11;
const STATUS_SUCCESS = 12;
const STATUS_FAILED = 13;


export default {
  name: 'BulkUploadOverview',
  components: { PageHeadline },
  methods: {
    hasAnyOfTheseRoles(roles) {
      return hasAnyOfTheseRoles(roles);
    },
    uploadStockList() {
      this.currentStatus = STATUS_SAVING;
      if (!this.stockList) {
        this.currentStatus = STATUS_FAILED;
      }
      const formData = new FormData();
      formData.append('file', this.stockList);
      store
          .dispatch('uploadXlsxFile', {
            file: this.stockList,
            url: constants.url.offerService.baseUrl + constants.url.offerService.api.purchaseOffers.upload,
          })
          .then(() => {
            this.currentStatus = STATUS_SUCCESS;
            this.$refs.stockListFileInput.reset();
            console.log('(offer-service) upload worked!');
          })
          .catch(() => {
            this.currentStatus = STATUS_FAILED;
            console.log('(offer-service) upload didn’t work');
          });
    },
    async getUploadedStockLists() {
      const options = {
        baseURL: constants.url.offerService.baseUrl,
        url: constants.url.offerService.api.purchaseOffers.uploads,
      };
      return apiAuth({
        method: 'GET',
        baseURL: options.baseURL,
        url: options.url,
      }).then(function(res) {
        return res.data.data;
      }).catch(function() {
        console.log('FAILURE!!');
      });
    },
    async downloadFile(type, fileName, displayName) {
      console.log(fileName);
      let url, fileType;
      switch (type) {
        case 'stockFile':
          url = constants.url.offerService.api.purchaseOffers.downloadStockFile(fileName);
          fileType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
          break;
        case 'importLog':
          url = constants.url.offerService.api.purchaseOffers.downloadImportLog(fileName)
          fileType = 'text/csv';
          break;
        case 'errorLog':
          url = constants.url.offerService.api.purchaseOffers.downloadErrorLog(fileName)
          fileType = 'text/csv';
          break;
      }
      return apiAuth({
        method: 'GET',
        baseURL: constants.url.offerService.baseUrl,
        url: url,
        responseType: 'blob',
      }).then((response) => {
        const blob = new Blob([response.data], {type : fileType});
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.setAttribute("download", displayName);
        a.click();
      }).catch(function(err) {
        console.log(`an error occured while trying to download the file ${fileName}`, err);
      });
    },
  },
  data: function() {
    return {
      uploadsDataFields: [
        {
          key: 'originalName',
          label: 'File',
        },
        {
          key: 'createdAt',
          label: 'Uploaded at',
        },
        {
          key: 'status',
          label: 'Status',
        },
      ],
      stockList: null,
      currentStatus: STATUS_INITIAL,
    };
  },
  computed: {
    isInitial() {
      return this.currentStatus === STATUS_INITIAL;
    },
    isSaving() {
      return this.currentStatus === STATUS_SAVING;
    },
    isSuccess() {
      return this.currentStatus === STATUS_SUCCESS;
    },
    isFailed() {
      return this.currentStatus === STATUS_FAILED;
    },
  },
};
</script>

<style scoped>
h2 {
  margin-bottom: 1rem;
}

.dashboard-content {
  min-height: 300px;
}

.download-button {
  cursor: pointer;
}
</style>
